import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, User, Phone, Mail, MapPin, Calendar, TrendingUp, ShoppingCart, CreditCard, Clock, Save, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { format, startOfDay, endOfDay } from "date-fns";
import { ptBR } from "date-fns/locale";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import { useTenant } from "@/contexts/TenantContext";
import { logCrud } from "@/services/activities";
import { getGravatarUrl } from "@/lib/gravatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type Client = {
  id: string;
  nome: string;
  cpf_cnpj: string | null;
  telefone: string | null;
  whatsapp: string | null;
  email: string | null;
  endereco_completo: string | null;
  data_nascimento: string | null;
  observacoes: string | null;
  created_at: string;
  chatwoot_contact_id: number | null;
  foto_url: string | null;
  chatwoot_avatar_url: string | null;
};

type Transaction = {
  id: string;
  tipo: string;
  valor_total: number;
  valor_liquido: number;
  desconto: number;
  status: string;
  created_at: string;
  descricao: string | null;
  pagamento_json?: any;
  transaction_items: {
    id: string;
    quantidade: number;
    preco_unitario: number;
    total: number;
    products: {
      nome: string;
    } | null;
  }[];
};

type ClientStats = {
  totalSpent: number;
  totalTransactions: number;
  averageTicket: number;
  lastPurchase: string | null;
  frequency: number;
  topProducts: { name: string; quantity: number; revenue: number }[];
  monthlySpending: { month: string; value: number }[];
  paymentMethods: { method: string; value: number }[];
};

type ChatwootMessage = {
  id: number;
  content: string;
  message_type: number;
  created_at: number;
  sender_type: "contact" | "user";
  sender_id: number;
  sender?: {
    name: string;
    email?: string;
    phone_number?: string;
  };
  conversation_id: number;
};

type ChatwootConversation = {
  id: number;
  status: string;
  created_at: number;
  updated_at: number;
  inbox_id: number;
};

const CHATWOOT_BASE_URL = import.meta.env.VITE_CHATWOOT_BASE_URL || "https://app.chatwoot.com";

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || isNaN(value)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", { 
    style: "currency", 
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

export default function ClientDetails(props?: { id?: string; embedded?: boolean; periodRange?: [Date, Date]; setLoading?: (loading: boolean) => void }) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = props?.id ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const { hasToken } = useChatwootToken();
  const { tenantId } = useTenant();
  const search = new URLSearchParams(location.search);
  const isEmbedded = (typeof props?.embedded !== 'undefined') ? !!props?.embedded : (search.get("embedded") === "1");
  const fromStr = search.get("from");
  const toStr = search.get("to");
  const [cadFrom, setCadFrom] = useState<Date>(props?.periodRange ? startOfDay(props.periodRange[0]) : (fromStr ? startOfDay(new Date(fromStr)) : startOfDay(new Date(0))));
  const [cadTo, setCadTo] = useState<Date>(props?.periodRange ? endOfDay(props.periodRange[1]) : (toStr ? endOfDay(new Date(toStr)) : endOfDay(new Date())));
  useEffect(() => {
    if (isEmbedded && !props?.periodRange) {
      const handler = (e: MessageEvent) => {
        const data = (e.data || {}) as any;
        if (data && data.type === 'rx-period-change') {
          setCadFrom(startOfDay(new Date(data.from)));
          setCadTo(endOfDay(new Date(data.to)));
        }
      };
      window.addEventListener('message', handler);
      return () => window.removeEventListener('message', handler);
    }
  }, [isEmbedded, props?.periodRange]);

  const { data: client, isLoading: clientLoading } = useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data as Client;
    },
    enabled: !!id,
  });

  // Buscar token e account_id do Chatwoot
  const { data: chatwootConfig } = useQuery({
    queryKey: ["chatwoot-config", tenantId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data: profile } = await supabase
        .from("profiles")
        .select("chatwoot_user_token, tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) return null;

      const { data: tenant } = await supabase
        .from("tenants")
        .select("chatwoot_account_id")
        .eq("id", profile.tenant_id)
        .maybeSingle();

      return {
        token: profile?.chatwoot_user_token || null,
        accountId: tenant?.chatwoot_account_id || null,
      };
    },
    enabled: !!tenantId && hasToken,
  });

  // Buscar conversations do cliente
  const { data: conversations, isLoading: conversationsLoading } = useQuery({
    queryKey: ["chatwoot-conversations", client?.chatwoot_contact_id, chatwootConfig?.accountId, chatwootConfig?.token],
    queryFn: async () => {
      if (!client?.chatwoot_contact_id || !chatwootConfig?.accountId || !chatwootConfig?.token) {
        return [];
      }

      try {
        const response = await fetch(
          `${CHATWOOT_BASE_URL}/api/v1/accounts/${chatwootConfig.accountId}/conversations?status=all`,
          {
            headers: {
              "api_access_token": chatwootConfig.token,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Erro ao buscar conversations: ${response.status}`);
        }

        const data = await response.json();
        // Filtrar conversations pelo contact_id
        const contactConversations = (data?.payload || []).filter((conv: any) => {
          return conv?.meta?.sender?.id === client.chatwoot_contact_id;
        });

        return contactConversations as ChatwootConversation[];
      } catch (error) {
        console.error("Erro ao buscar conversations:", error);
        return [];
      }
    },
    enabled: !!client?.chatwoot_contact_id && !!chatwootConfig?.accountId && !!chatwootConfig?.token && hasToken,
  });

  // Buscar mensagens de todas as conversations
  const { data: allMessages, isLoading: messagesLoading } = useQuery({
    queryKey: ["chatwoot-messages", conversations, chatwootConfig?.accountId, chatwootConfig?.token],
    queryFn: async () => {
      if (!conversations || conversations.length === 0 || !chatwootConfig?.accountId || !chatwootConfig?.token) {
        return [];
      }

      try {
        const messagesPromises = conversations.map(async (conv) => {
          const response = await fetch(
            `${CHATWOOT_BASE_URL}/api/v1/accounts/${chatwootConfig.accountId}/conversations/${conv.id}/messages`,
            {
              headers: {
                "api_access_token": chatwootConfig.token,
              },
            }
          );

          if (!response.ok) {
            console.warn(`Erro ao buscar mensagens da conversation ${conv.id}: ${response.status}`);
            return [];
          }

          const data = await response.json();
          return (data?.payload || []).map((msg: any) => ({
            ...msg,
            conversation_id: conv.id,
          })) as ChatwootMessage[];
        });

        const messagesArrays = await Promise.all(messagesPromises);
        // Flatten e ordenar por data
        const allMessages = messagesArrays.flat();
        return allMessages.sort((a, b) => a.created_at - b.created_at);
      } catch (error) {
        console.error("Erro ao buscar mensagens:", error);
        return [];
      }
    },
    enabled: !!conversations && conversations.length > 0 && !!chatwootConfig?.accountId && !!chatwootConfig?.token,
  });

  const { data: transactions, isLoading: transactionsLoading } = useQuery({
    queryKey: ["client-transactions", id, cadFrom.toISOString(), cadTo.toISOString()],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select(`
          *,
          transaction_items (
            id,
            quantidade,
            preco_unitario,
            total,
            products (nome)
          )
        `)
        .eq("client_id", id)
        .gte("created_at", cadFrom.toISOString())
        .lte("created_at", cadTo.toISOString())
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!id,
  });

  useEffect(() => {
    const loading = clientLoading || transactionsLoading;
    if (isEmbedded) {
      window.parent?.postMessage({ type: 'rx-loading', loading }, '*');
    }
    if (props?.setLoading) props.setLoading(loading);
  }, [clientLoading, transactionsLoading, isEmbedded, props?.setLoading]);

  const { data: stats } = useQuery({
    queryKey: ["client-stats", id],
    queryFn: async () => {
      if (!transactions) return null;

      const sales = transactions.filter(t => t.tipo === "entrada");
      const totalSpent = sales.reduce((sum, t) => sum + Number(t.valor_liquido), 0);
      const totalTransactions = sales.length;
      const averageTicket = totalTransactions > 0 ? totalSpent / totalTransactions : 0;
      const lastPurchase = sales.length > 0 ? sales[0].created_at : null;

      // Calcular frequência (compras nos últimos 30 dias)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const frequency = sales.filter(t => new Date(t.created_at) >= thirtyDaysAgo).length;

      // Top produtos
      const productMap = new Map<string, { quantity: number; revenue: number }>();
      sales.forEach(sale => {
        sale.transaction_items?.forEach(item => {
          const productName = item.products?.nome || "Produto Desconhecido";
          const current = productMap.get(productName) || { quantity: 0, revenue: 0 };
          productMap.set(productName, {
            quantity: current.quantity + item.quantidade,
            revenue: current.revenue + Number(item.total),
          });
        });
      });
      const topProducts = Array.from(productMap.entries())
        .map(([name, data]) => ({ name, ...data }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Gastos mensais (últimos 6 meses)
      const monthlyMap = new Map<string, number>();
      sales.forEach(sale => {
        const month = format(new Date(sale.created_at), "MMM/yy", { locale: ptBR });
        monthlyMap.set(month, (monthlyMap.get(month) || 0) + Number(sale.valor_liquido));
      });
      const monthlySpending = Array.from(monthlyMap.entries())
        .map(([month, value]) => ({ month, value }))
        .slice(-6);

      // Métodos de pagamento (a partir de transaction_payments confirmados)
      const salesIds = sales.map(s => s.id);
      let paymentMethods: { method: string; value: number }[] = [];
      if (salesIds.length > 0) {
        const { data: pays } = await supabase
          .from('transaction_payments')
          .select('payment_method_id, valor, status, transaction_id')
          .in('transaction_id', salesIds)
          .eq('status', 'confirmado');

        const { data: pmRef } = await supabase
          .from('payment_methods')
          .select('id, nome');
        const nameById = new Map<string, string>();
        (pmRef || []).forEach((m: any) => nameById.set(m.id, m.nome));

        const paymentMap = new Map<string, number>();
        (pays || []).forEach((p: any) => {
          const id = p?.payment_method_id as string | null;
          const method = id ? (nameById.get(id) || id) : 'Não especificado';
          const valor = Number(p?.valor || 0);
          paymentMap.set(method, (paymentMap.get(method) || 0) + valor);
        });
        paymentMethods = Array.from(paymentMap.entries()).map(([method, value]) => ({ method, value }));
      }

      return {
        totalSpent,
        totalTransactions,
        averageTicket,
        lastPurchase,
        frequency,
        topProducts,
        monthlySpending,
        paymentMethods,
      } as ClientStats;
    },
    enabled: !!transactions,
  });

  const updateMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const { error } = await supabase
        .from("clients")
        .update({
          nome: formData.get("nome") as string,
          cpf_cnpj: (formData.get("cpf_cnpj") as string) || null,
          telefone: (formData.get("telefone") as string) || null,
          whatsapp: (formData.get("whatsapp") as string) || null,
          email: (formData.get("email") as string) || null,
          endereco_completo: (formData.get("endereco_completo") as string) || null,
          data_nascimento: (formData.get("data_nascimento") as string) || null,
          observacoes: (formData.get("observacoes") as string) || null,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client", id] });
      toast.success("Cliente atualizado com sucesso");
      if (id) {
        void logCrud({ entitySlug: "entity:clients", operationSlug: "update", entityId: id, screenSlug: "cliente" });
      }
      setIsEditing(false);
    },
    onError: () => toast.error("Erro ao atualizar cliente"),
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    updateMutation.mutate(formData);
  };

  if (clientLoading) {
    return <div className="p-8">Carregando...</div>;
  }

  if (!client) {
    return <div className="p-8">Cliente não encontrado</div>;
  }

  const COLORS = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))', 'hsl(var(--card))'];

  return (
    <div className="space-y-6">
      {/* Header */}
      {!isEmbedded && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/clientes")}> 
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-4">
              <Avatar className="h-20 w-20">
                {(client.foto_url || client.chatwoot_avatar_url || getGravatarUrl(client.email)) && (
                  <AvatarImage 
                    src={client.foto_url || client.chatwoot_avatar_url || getGravatarUrl(client.email)!} 
                    alt={client.nome} 
                  />
                )}
                <AvatarFallback className="text-2xl">
                  {client.nome
                    .split(" ")
                    .map((n) => n[0])
                    .slice(0, 2)
                    .join("")
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-3xl font-bold">{client.nome}</h1>
                <p className="text-muted-foreground">
                  Cliente desde {format(new Date(client.created_at), "dd/MM/yyyy")}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* KPIs Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Gasto</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.totalSpent.toFixed(2)}` : "-"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ticket Médio</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats ? `R$ ${stats.averageTicket.toFixed(2)}` : "-"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Compras</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.totalTransactions || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Última Compra</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.lastPurchase 
                ? format(new Date(stats.lastPurchase), "dd/MM/yyyy")
                : "Nunca"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className={`grid w-full ${hasToken ? "grid-cols-6" : "grid-cols-5"}`}>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="sales">Vendas</TabsTrigger>
          <TabsTrigger value="data">Dados Cadastrais</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          {hasToken && <TabsTrigger value="messages">Mensagens</TabsTrigger>}
        </TabsList>

        {/* Visão Geral */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Informações de Contato
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {client.telefone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.telefone}</span>
                  </div>
                )}
                {client.whatsapp && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{client.whatsapp} (WhatsApp)</span>
                  </div>
                )}
                {client.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{client.email}</span>
                  </div>
                )}
                {client.endereco_completo && (
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{client.endereco_completo}</span>
                  </div>
                )}
                {client.data_nascimento && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{format(new Date(client.data_nascimento), "dd/MM/yyyy")}</span>
                  </div>
                )}
                {client.cpf_cnpj && (
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">CPF/CNPJ:</span>
                    <span>{client.cpf_cnpj}</span>
                  </div>
                )}
                {client.observacoes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-sm font-medium mb-2">Observações:</p>
                      <p className="text-sm text-muted-foreground">{client.observacoes}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Top Produtos */}
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Produtos Comprados</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.topProducts && stats.topProducts.length > 0 ? (
                  <div className="space-y-3">
                    {stats.topProducts.map((product, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">{product.quantity}x unidades</p>
                        </div>
                        <Badge variant="secondary">
                          R$ {product.revenue.toFixed(2)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">Nenhuma compra realizada</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Gastos Mensais */}
            <Card>
              <CardHeader>
                <CardTitle>Evolução de Gastos</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.monthlySpending && stats.monthlySpending.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={stats.monthlySpending}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de gastos</p>
                )}
              </CardContent>
            </Card>

            {/* Métodos de Pagamento */}
            <Card>
              <CardHeader>
                <CardTitle>Métodos de Pagamento</CardTitle>
              </CardHeader>
              <CardContent>
                {stats?.paymentMethods && stats.paymentMethods.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={stats.paymentMethods}
                        dataKey="value"
                        nameKey="method"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(entry: any) => formatCurrency(entry.value)}
                      >
                        {stats.paymentMethods.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: number) => formatCurrency(value)}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-muted-foreground text-center py-12">Sem dados de pagamento</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Vendas */}
        <TabsContent value="sales" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              {transactionsLoading ? (
                <p className="text-center py-8">Carregando...</p>
              ) : transactions && transactions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Produtos</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {format(new Date(transaction.created_at), "dd/MM/yyyy HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Badge variant={transaction.tipo === "entrada" ? "default" : "secondary"}>
                            {transaction.tipo}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {transaction.transaction_items?.length || 0} item(s)
                        </TableCell>
                        <TableCell>
                          <Badge variant={transaction.status === "confirmada" ? "default" : "outline"}>
                            {transaction.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          R$ {Number(transaction.valor_liquido).toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma venda registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dados Cadastrais */}
        <TabsContent value="data" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Dados Cadastrais</CardTitle>
              {!isEditing && (
                <Button variant="outline" onClick={() => setIsEditing(true)}>
                  Editar
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nome">Nome Completo/Razão Social *</Label>
                      <Input id="nome" name="nome" defaultValue={client.nome} required />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
                      <Input id="cpf_cnpj" name="cpf_cnpj" defaultValue={client.cpf_cnpj || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <Input id="telefone" name="telefone" defaultValue={client.telefone || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp">WhatsApp</Label>
                      <Input id="whatsapp" name="whatsapp" defaultValue={client.whatsapp || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-mail</Label>
                      <Input id="email" name="email" type="email" defaultValue={client.email || ""} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="data_nascimento">Data de Nascimento</Label>
                      <Input id="data_nascimento" name="data_nascimento" type="date" defaultValue={client.data_nascimento || ""} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endereco_completo">Endereço Completo</Label>
                    <Textarea id="endereco_completo" name="endereco_completo" defaultValue={client.endereco_completo || ""} rows={2} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="observacoes">Observações</Label>
                    <Textarea id="observacoes" name="observacoes" defaultValue={client.observacoes || ""} rows={3} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit">
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Alterações
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                      Cancelar
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Nome</p>
                      <p>{client.nome}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">CPF/CNPJ</p>
                      <p>{client.cpf_cnpj || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                      <p>{client.telefone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">WhatsApp</p>
                      <p>{client.whatsapp || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">E-mail</p>
                      <p>{client.email || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Data de Nascimento</p>
                      <p>{client.data_nascimento ? format(new Date(client.data_nascimento), "dd/MM/yyyy") : "-"}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Endereço</p>
                    <p>{client.endereco_completo || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Observações</p>
                    <p>{client.observacoes || "-"}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline */}
        <TabsContent value="timeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Timeline de Atividades</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions && transactions.length > 0 ? (
                <div className="relative border-l-2 border-muted pl-6 space-y-6">
                  {transactions.map((transaction, index) => (
                    <div key={transaction.id} className="relative">
                      <div className="absolute -left-[29px] w-4 h-4 rounded-full bg-primary border-4 border-background" />
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(transaction.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        </p>
                        <p className="font-medium">
                          {transaction.tipo === "entrada" ? "Compra realizada" : "Devolução"}
                        </p>
                        <div className="text-sm">
                          <p>Valor: R$ {Number(transaction.valor_liquido).toFixed(2)}</p>
                          <p>Status: {transaction.status}</p>
                          {transaction.descricao && <p className="text-muted-foreground">{transaction.descricao}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">Nenhuma atividade registrada</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Mensagens */}
        {hasToken && (
          <TabsContent value="messages" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Mensagens WhatsApp Trocadas com este Cliente
                </CardTitle>
              </CardHeader>
              <CardContent>
                {conversationsLoading || messagesLoading ? (
                  <p className="text-center py-8">Carregando mensagens...</p>
                ) : !client?.chatwoot_contact_id ? (
                  <p className="text-muted-foreground text-center py-8">
                    Cliente não está vinculado ao Chatwoot. Sincronize o cliente para visualizar as mensagens.
                  </p>
                ) : allMessages && allMessages.length > 0 ? (
                  <div className="space-y-4">
                    {allMessages.map((message, index) => {
                      const isContact = message.sender_type === "contact";
                      const messageDate = new Date(message.created_at * 1000);
                      const prevMessage = index > 0 ? allMessages[index - 1] : null;
                      const prevDate = prevMessage ? new Date(prevMessage.created_at * 1000) : null;
                      const showDateSeparator = !prevDate || 
                        messageDate.toDateString() !== prevDate.toDateString();

                      return (
                        <div key={`${message.conversation_id}-${message.id}`}>
                          {showDateSeparator && (
                            <div className="flex items-center gap-2 my-4">
                              <Separator className="flex-1" />
                              <span className="text-xs text-muted-foreground">
                                {format(messageDate, "dd/MM/yyyy", { locale: ptBR })}
                              </span>
                              <Separator className="flex-1" />
                            </div>
                          )}
                          <div
                            className={`flex gap-3 p-3 rounded-lg ${
                              isContact
                                ? "bg-muted"
                                : "bg-primary/10"
                            }`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-sm font-medium">
                                  {isContact
                                    ? client.nome
                                    : message.sender?.name || "Agente"}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {format(messageDate, "HH:mm", { locale: ptBR })}
                                </span>
                              </div>
                              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Nenhuma mensagem encontrada para este cliente.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Analytics */}
        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Frequência de Compra</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.frequency || 0}</div>
                <p className="text-sm text-muted-foreground">Compras nos últimos 30 dias</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Lifetime Value (LTV)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  R$ {stats?.totalSpent.toFixed(2) || "0.00"}
                </div>
                <p className="text-sm text-muted-foreground">Valor total investido</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engajamento</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {stats?.totalTransactions 
                    ? ((stats.frequency / stats.totalTransactions) * 100).toFixed(0)
                    : 0}%
                </div>
                <p className="text-sm text-muted-foreground">Taxa de recompra recente</p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Produtos Mais Comprados (Quantidade)</CardTitle>
            </CardHeader>
            <CardContent>
              {stats?.topProducts && stats.topProducts.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stats.topProducts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="quantity" fill="hsl(var(--primary))" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-muted-foreground text-center py-12">Sem dados disponíveis</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
